import { isFunction } from 'lodash-es'

import type { ActionCommand } from '../types'

/**
 * How well a command answers a query. Zero means it does not.
 *
 * Substring matching was enough for nine hand-written commands. It is not enough
 * for the ~86 sidebar pages: "printers" misses "Printer Reports" on the trailing
 * s alone, and a two-word query matched nothing at all, because the whole query
 * was tested against the whole label.
 *
 * So a query is split into tokens and **every token must score**. That is what
 * keeps "printer" from dragging in a page that only answers "settings", and what
 * lets a section name narrow a query — "finance settings" reaches Settings under
 * Finance, where "settings" alone reaches every one of them.
 *
 * A section name only ever narrows. At least one token must answer the label or
 * the keywords, or the command scores nothing — see `scoreCommand`.
 */
const LABEL_EXACT = 100
const LABEL_PREFIX = 80
const LABEL_WORD_PREFIX = 60
const LABEL_CONTAINS = 40
const KEYWORD_EXACT = 35
const KEYWORD_PREFIX = 25
const SUB_LABEL_CONTAINS = 10

/** An empty query shows every command, as it always has. Equal scores keep declaration order. */
const NEUTRAL = 1

/**
 * A token that only matches once its trailing `s` is dropped scores slightly
 * lower, so "Printers" still outranks "Printer Reports" for the query "printers".
 * Deliberately not a stemmer — one rule, covering the plural that people type.
 */
const SINGULAR_PENALTY = 0.9
const SHORTEST_STEMMABLE = 4

interface MatchFields {
  label: string
  labelWords: string[]
  keywords: string[]
  subLabel: string
}

const scoreCommand = (action: ActionCommand, query: string, hasValidBarcodesInQuery: boolean): number => {
  if (isPinnedBarcodeAction(action, query, hasValidBarcodesInQuery)) {
    return LABEL_EXACT
  }

  const tokens = tokenise(query)

  if (!tokens.length) {
    return NEUTRAL
  }

  const fields = matchFieldsOf(action)
  let total = 0
  let answeredDirectly = false

  for (const token of tokens) {
    const direct = withSingularRetry(token, candidate => scoreDirect(candidate, fields))

    if (direct) {
      total += direct
      answeredDirectly = true
      continue
    }

    const section = withSingularRetry(token, candidate => scoreSection(candidate, fields))

    if (!section) {
      return 0
    }

    total += section
  }

  // A section name cannot carry a row on its own. Without this, "finance"
  // answers all seventeen Finance pages at the same score, and the eight-row cap
  // then keeps whichever have the shortest labels. "print" was worse: eleven
  // pages answered it on "POS & Printing" alone, and the cap dropped
  // Production Centers — a page the query is actually about — to keep Devices
  // and License Keys, which it is not.
  return answeredDirectly ? total : 0
}

/**
 * The barcode actions answer a scanned code rather than a typed word, so they
 * stay visible on their own terms. Both cases predate scoring and are unchanged.
 */
const isPinnedBarcodeAction = (action: ActionCommand, query: string, hasValidBarcodesInQuery: boolean): boolean => {
  if (hasValidBarcodesInQuery) {
    return action.id === 'action-generate-family-static' || action.id === 'action-initiate-add-variants'
  }

  return action.id === 'action-generate-family-static' && !query
}

/** Retries the token without its trailing `s` when the token itself scores nothing. */
const withSingularRetry = (token: string, score: (candidate: string) => number): number => {
  const direct = score(token)

  if (direct) {
    return direct
  }

  const stem = singular(token)

  return stem === token ? 0 : score(stem) * SINGULAR_PENALTY
}

/** The command's own words — what it is called and what it was declared to answer. */
const scoreDirect = (token: string, { label, labelWords, keywords }: MatchFields): number => {
  if (label === token) return LABEL_EXACT
  if (label.startsWith(token)) return LABEL_PREFIX
  if (labelWords.some(word => word.startsWith(token))) return LABEL_WORD_PREFIX
  if (label.includes(token)) return LABEL_CONTAINS
  if (keywords.includes(token)) return KEYWORD_EXACT
  if (keywords.some(keyword => keyword.startsWith(token))) return KEYWORD_PREFIX

  return 0
}

const scoreSection = (token: string, { subLabel }: MatchFields): number =>
  subLabel.includes(token) ? SUB_LABEL_CONTAINS : 0

const singular = (token: string): string =>
  token.length >= SHORTEST_STEMMABLE && token.endsWith('s') ? token.slice(0, -1) : token

const tokenise = (query: string): string[] => query.toLowerCase().split(/\s+/).filter(Boolean)

const matchFieldsOf = (action: ActionCommand): MatchFields => {
  const label = labelOf(action).toLowerCase()

  return {
    label,
    labelWords: label.split(/\s+/).filter(Boolean),
    keywords: keywordsOf(action),
    // On a navigation row this holds the sidebar section, so "finance invoices" works.
    subLabel: (action.subLabel ?? '').toLowerCase()
  }
}

const labelOf = (action: ActionCommand): string => (isFunction(action.label) ? action.label() : (action.label ?? ''))

/**
 * Flattened to single words, so a two-word keyword like `'product set'` answers
 * "set" as well as "product".
 */
const keywordsOf = (action: ActionCommand): string[] => {
  const declared = Array.isArray(action.keywords) ? action.keywords : [action.keywords ?? '']

  return declared.flatMap(keyword => tokenise(keyword))
}

export { scoreCommand }
